import { IDataReaderClient } from "@multiweb/clients/dataReader";
import { StringComparisonTypes } from "Configuration/AppConfiguration";
import { FunctionUtils } from "Global/Base/Utils/FunctionUtils";
import { AttrValueReader } from "OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IRecordIdentification } from "OW.Remoting/Remoting/DataTypes/RecordIdentification";
import { IDataLoadingAwaitable } from "UI/UI.BaseControls/ControlInterfaces";
import { IDataGridDataShownEvent, IDataGridSelectionChangedEvent } from "UI/UI.Controls/IEvent";
import { FormManager } from "../FormManager/IFormManager";
import { UIManager } from "../UIManager";
import { FormResultType } from "../XGControls/Forms/FormResult";
import { ModalWindowType } from "../XGControls/ModalWindow";
import { ControlWithLoggerParams, IEditableControl, IRequiresActualObject } from "./ControlInterfaces";
import { LOVEditMaster } from "./LOVEditMaster";

export type TextLOVEditParams = ControlWithLoggerParams & {
    dataReaderClient: IDataReaderClient;
};

export class TextLOVEdit extends LOVEditMaster implements IEditableControl, IRequiresActualObject, IDataLoadingAwaitable {
    //#region Fields
    private readonly _showAttr: string;
    private readonly _additionalAttrs: string[];
    private readonly _showAdditionalAttrs: string[];
    private readonly _dataReaderClient: IDataReaderClient;

    private get _hasWhisperer(): boolean {
        return !!this.className;
    }
    //#endregion Fields

    //#region Properties
    public set actualObject(value: OWObject) {
        this._actualObject = value;
        if (value == null) {
            this._updateValues(null);
        }
    }

    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public get isDisabled(): boolean {
        return this.element.classList.contains("Disabled");
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: TextLOVEditParams) {
        super(params);
        this._dataReaderClient = params.dataReaderClient;
        this._showAttr = this.getDataAttr("ShowAttr");
        if (!this._showAttr)
            throw new Error(`TextLOVEdit id ${this.element.id} property ShowAttr is required but empty`);

        this._additionalAttrs = this.getDataAttr("AdditionalAttrs")?.split(";") ?? [];
        this._showAdditionalAttrs = this.getDataAttr("ShowAdditionalAttrs")?.split(";") ?? [];
        if (this._additionalAttrs.length !== this._showAdditionalAttrs.length)
            throw new Error(`TextLOVEdit id ${this.element.id} property ShowAdditionalAttrs and AdditionalAttrs have different number of items`);

        if (this._condition) {
            this._mainCondition = "(" + this._condition + ")";
        }

        this.className = this.getDataAttr("ValidationClassName") || null;
        if (!this.className)
            params.logger.warn("Component is missing 'ValidClassName' property. Whisperer and AdditionalAttrs/ShowAdditionalAttrs functionality is disabled.");

        this._setListeners();
    }
    //#endregion Constructors

    //#region Methods
    protected override _createWhispererColumns(): void {
        this._createColumns([this._showAttr]);
    }

    private _setListeners(): void {
        this._setDefaultListeners();

        this._events.addListener(this._lovButton, "click", FunctionUtils.debouncedFunction(async (event: MouseEvent) => {
            if (!this._checkClickEvent(event))
                return;
            UIManager.instance.showLoader();
            try {
                this.focus(); //v některých prohlížečích mohlo dojít ke ztrátě focusu při kliku na lovbutton

                this._lovButtonClicked = true;
                if (this.isEditing || this.enabled) {
                    if (!this.interactive) {
                        return; //přes form je vykresleno jiné modalní okno
                    } else {
                        if (this.actualObject == null) {
                            return;
                        }
                        if (this.isDisabled && this.controlValue == "") {
                            return;
                        }

                        if (this._whisperer) {
                            this._whisperer.clearDataRows();
                        }

                        await FormManager.instance.openFormModal({
                            formName: this._editWindow,
                            type: ModalWindowType.lov,
                            onClose: (_sender, result) => {
                                if (result && result.resultType === FormResultType.ok && result.resultValue instanceof IRecordIdentification) {
                                    this._updateValues(result.resultValue);
                                }

                                self.setTimeout(() => {
                                    this.innerElement.focus();
                                }, 0);
                            },
                            parametricData: {
                                condition: this._condition,
                                conditionParamsAttrs: this._conditionParamsAttrs,
                                conditionParamsObject: this.actualObject,
                                viewMode: false,
                                inLOVMode: true,
                                enableLOVSelect: !this.isDisabled,
                                applyLOVCondition: true,
                                autoSelectFirstRow: true,
                                masterObject: null,
                                masterLinkToAttr: null,
                                setValues: null,
                                selectedID: null,
                                openInEdit: false,
                                showDetail: this._showDetail,
                                ownerForm: this._ownerForm, // Jinak na něm nepůjde zakázat/skrýt backButton formuláře
                                formContext: this.frameContext,
                            },
                        });
                    }
                }
            } finally {
                UIManager.instance.hideLoader();
                this._isModalLoading = false;
            }
        }, 100));

        this._events.addListener(this.element, "focusout", (event: FocusEvent) => {
            this.lostFocus.trigger(this, event);
            this._lovOnBlur(event.relatedTarget !== null);
        });

        this._events.addListener(this.innerElement, "blur", (event: FocusEvent) => {
            this.lostFocus.trigger(this, event);
            this._lovOnBlur(event.relatedTarget !== null);
        });

        if (this._hasWhisperer)
            this._events.addListener(this.innerElement, "keyup", (e: KeyboardEvent) => {
                this._keyboardEventHandler(e);
            });
    }

    private async _updateValues(recordIdentification: IRecordIdentification | null): Promise<void> {
        if (!recordIdentification) {
            this.controlValue = null;
            if (this._whisperer)
                this._whisperer.clearDataRows();
            return;
        }

        const recordData = (await this._dataReaderClient.getDataRowsWithParams(
            recordIdentification.className,
            [this._showAttr, ...this._showAdditionalAttrs],
            "ID = @0",
            {
                params: [ recordIdentification.id ],
                count: 1,
                dataLength: -1,
            },
        ))[0];

        const value = AttrValueReader.readRow(recordData, this._showAttr);
        this.actualObject.setValue(this.attrName, value);
        this.controlValue = value ? value.toString() : "";

        if (this._additionalAttrs.length > 0) {
            for (const [i, additionalAttr] of this._additionalAttrs.entries()) {
                const additionalValue = AttrValueReader.readRow(recordData, this._showAdditionalAttrs[i]);
                this.actualObject.setValue(additionalAttr, additionalValue ?? null);
            }
        }
    }

    public override async waitOnDataLoaded(): Promise<boolean> {
        return this._waitOnDataLoadedDeffer.promise;
    }

    /**
     * Metoda volaná při změně objektu (výběrem z našeptávače nebo z modalního okna)
     * @param value
     * @param callingEvent
     * @param btnClick - pokud je vyvoláno klikem na tlačítko LOV tak je true
     * @returns boolean promise - status průběhu, true v pořádku
     */
    protected override async _changeObject(value: string, callingEvent: Event, btnClick: boolean = false): Promise<boolean> {
        if (!this._dataSource) {
            return false;
        }
        const cond: string = value ? this._buildCondition(value) : "";
        if (!await super._changeObject(cond, callingEvent, btnClick)) {
            return false;
        }

        this._dataSource.setAttrs([this._showAttr]);
        await this._dataSource.loadNewData(false);
        return true;
    }

    /**
     * Inicializace našeptávače (datagridu)
     */
    protected override async _initializeWhisperer(): Promise<boolean> {
        if (!await super._initializeWhisperer()) {
            return false;
        }

        this._whisperer.registerEventListener("SelectionChanged", async (e: IDataGridSelectionChangedEvent) => {
            if (this._whisperer.dataRows.length > 0 && e.detail.rowID != "-1") {
                this._updateValues({ id: e.detail.rowID, className: this.className });
                this.hideDropDown();
                this._whisperer.clearSelectedRows();
            }
        });

        this._whisperer.registerEventListener("DataShown", async (_e: IDataGridDataShownEvent) => {
            if (this._hasFocus && ((this._whisperer.dataRows.length > 0 && this._lovButtonClicked) || (this._whisperer.dataRows.length > 1) && ((this.innerElement.value != "") || ((this.innerElement.value == "") && (this._lovButtonClicked || this._showByKey))))) {
                await this.showDropDown();
            } else {
                if (this._whisperer && this._whisperer.dataRows.length == 1) {
                    this._updateValues({ id: this._whisperer.dataRows[0].dataKey, className: this.className });
                    this.hideDropDown();
                }
            }

            this._lovButtonClicked = false;
            this._waitOnDataLoadedDeffer.resolve();
        });
        return true;
    }

    private _buildCondition(param: string): string {
        let result: string = "";

        if (param) {
            const paramsValue = this._defaultWhisperer === StringComparisonTypes.startsWith ? `${param}%` : `%${param}%`;
            result = `:Linguistic.LIKE(${this._showAttr}, '${paramsValue}')`;
        }

        return result;
    }
    //#endregion Methods

    //#region Dispose
    public override dispose(): void {
        this._actualObject = null;
        this._dropDown?.element.remove();
        this._whisperer?.dispose();
        super.dispose();
    }
    //#endregion Dispose
}
